import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Breakpoint } from '@mui/material';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';

import ModalNotification, {
  ModalNotificationProps,
} from '@/shared/components/dialogs/ModalNotification';
import Queue from '@/shared/types/fulfilment/queue.type';
import Order from '@/shared/types/order/order.type';

import CustomsProductTableForm from './components/CustomsProductTableForm/CustomsProductTableForm';
import ProductTableRow from './components/CustomsProductTableForm/types/productTableRow.type';
import PackagingDetailsTableForm_v2 from './components/PackagingDetailsTableForm_v2/PackagingDetailsTableForm_v2';
import usePackagingDetailsTableForm_v2 from './components/PackagingDetailsTableForm_v2/hooks/usePackagingDetailsTableForm_v2';
import PickupDetailsForm from './components/PickupDetailsForm/PickupDetailsForm';
import usePickupDetailsForm from './components/PickupDetailsForm/hooks/usePickupDetailsForm';
import Review from './components/Review';
import SetOriginForm from './components/SetOriginForm/SetOriginForm';
import useSetOriginForm from './components/SetOriginForm/hooks/useSetOriginForm';
import TaxRecipientForm from './components/TaxRecipientForm/TaxRecipientForm';
import useTaxRecipientForm from './components/TaxRecipientForm/hooks/useTaxRecipientForm';
import useStepper from './hooks/useStepper';
import useSubmitToFulfilment from './hooks/useSubmitToFulfilment';
import getSubmitRequestBody from './utils/getSubmitRequestBody';

type SubmitToFulfilmentProps = {
  selectedQueues: Queue[];
  order: Order;
  loading: boolean;
  errors: string[];
  onClose: () => void;
  proceed: (values: Record<string, unknown>) => void;
};

const SubmitToFulfilment: FC<SubmitToFulfilmentProps> = ({
  selectedQueues,
  order,
  loading,
  errors,
  onClose,
  proceed,
}) => {
  const { t } = useTranslation(['orders', 'common']);

  const { fulfilmentProviderOrigins, shippingProfiles } = useSubmitToFulfilment(
    {
      order,
    },
  );

  const packagesDetailsTableState = usePackagingDetailsTableForm_v2();

  const [customProductsTableData, setCustomsProductTableData] = useState<
    ProductTableRow[]
  >([]);

  const taxRecipientState = useTaxRecipientForm();

  const originFormState = useSetOriginForm({
    shippingProfiles,
    fulfilmentProviderOrigins,
    order,
  });

  const pickupDetailsState = usePickupDetailsForm({
    fulfilmentOrigin: fulfilmentProviderOrigins?.find(
      (item) =>
        item.fulfilment_origin_id ===
        originFormState.pickOriginForm.fulfilmentOrigin,
    ),
  });

  const { currentStep, isFirstStep, isLastStep, handleNext, handlePrev } =
    useStepper({
      initialStep: 0,
      totalSteps: originFormState.pickOriginForm.useCustomsBroker ? 6 : 4,
    });

  const stepsConfigWithoutCustoms = [
    {
      size: 'sm',
      title: 'Submit to Fulfilment: Pick Origin',
      content: (
        <SetOriginForm
          key='set-origin-form'
          setOriginFormState={originFormState}
        />
      ),
    },
    {
      size: 'lg',
      title: 'Submit to Fulfilment: Packaging Details',
      content: (
        <PackagingDetailsTableForm_v2
          key='packaging-details-table-form'
          packagingDetailsTableForm_v2State={packagesDetailsTableState}
        />
      ),
    },
    {
      size: 'sm',
      title: 'Submit to Fulfilment: Pickup Details',
      content: (
        <PickupDetailsForm
          key='pickup-details-form'
          pickupDetailsFormState={pickupDetailsState}
        />
      ),
    },
    {
      size: 'sm',
      title: 'Submit to Fulfilment: Review',
      content: (
        <Review
          order={order}
          customProductsTableData={customProductsTableData}
          selectedQueues={selectedQueues}
          packagesDetailsTableState={packagesDetailsTableState}
          pickupDetailsState={pickupDetailsState}
          taxRecipientState={taxRecipientState}
          originFormState={originFormState}
          isCustoms={originFormState.pickOriginForm.useCustomsBroker}
        />
      ),
    },
  ];

  const stepsConfigWithCustoms = [
    {
      size: 'sm',
      title: 'Submit to Fulfilment: Pick Origin',
      content: (
        <SetOriginForm
          key='set-origin-form'
          setOriginFormState={originFormState}
        />
      ),
    },
    {
      size: 'sm',
      title: 'Submit to Fulfilment: Customs - Tax Recipient',
      content: (
        <TaxRecipientForm
          key='tax-recipient-form'
          taxRecipientFormState={taxRecipientState}
        />
      ),
    },
    {
      size: 'lg',
      title: 'Submit to Fulfilment: Customs Products',
      content: (
        <CustomsProductTableForm
          key='customs-product-table-form'
          getProductTableData={setCustomsProductTableData}
          onAddPackage={packagesDetailsTableState.handleAddPackage}
          selectedQueues={selectedQueues}
          packages={packagesDetailsTableState.packages}
          handleAssignItemsToPackage={
            packagesDetailsTableState.handleAssignItemsToPackage
          }
        />
      ),
    },
    {
      size: 'lg',
      title: 'Submit to Fulfilment: Packaging Details',
      content: (
        <PackagingDetailsTableForm_v2
          key='packaging-details-table-form'
          packagingDetailsTableForm_v2State={packagesDetailsTableState}
        />
      ),
    },
    {
      size: 'sm',
      title: 'Submit to Fulfilment: Pickup Details',
      content: (
        <PickupDetailsForm
          key='pickup-details-form'
          pickupDetailsFormState={pickupDetailsState}
        />
      ),
    },
    {
      size: 'sm',
      title: 'Submit to Fulfilment: Review',
      content: (
        <Review
          order={order}
          customProductsTableData={customProductsTableData}
          packagesDetailsTableState={packagesDetailsTableState}
          pickupDetailsState={pickupDetailsState}
          taxRecipientState={taxRecipientState}
          originFormState={originFormState}
          isCustoms={originFormState.pickOriginForm.useCustomsBroker}
        />
      ),
    },
  ];

  const hasErrors = errors.length > 0;

  const getActionsSet =
    (): ModalNotificationProps['dialogProps']['actions'] => {
      if (hasErrors) {
        return [
          {
            label: t('common:close'),
            onClick: onClose,
          },
        ];
      }

      if (isFirstStep) {
        return [
          {
            label: t('common:cancel'),
            onClick: onClose,
          },
          {
            label: t('common:next'),
            onClick: handleNext,
          },
        ];
      } else if (isLastStep) {
        return [
          {
            label: t('common:back'),
            onClick: handlePrev,
          },
          {
            label: t('common:submit'),
            variant: 'contained',
            icon: loading ? (
              <CircularProgress
                size='small'
                sx={{ color: '#fff', width: 16, height: 16, mr: 1 }}
              />
            ) : null,
            onClick: () => {
              if (!loading) {
                proceed(
                  getSubmitRequestBody({
                    order,
                    customProductsTableData,
                    selectedQueues,
                    packagesDetailsTableState,
                    pickupDetailsState,
                    taxRecipientState,
                    originFormState,
                    isCustoms: originFormState.pickOriginForm.useCustomsBroker,
                  }),
                );
              }
            },
          },
        ];
      } else {
        return [
          {
            label: t('common:back'),
            onClick: handlePrev,
          },
          {
            label: t('common:next'),
            onClick: handleNext,
          },
        ];
      }
    };

  const stepsConfig = originFormState.pickOriginForm.useCustomsBroker
    ? stepsConfigWithCustoms
    : stepsConfigWithoutCustoms;

  return (
    <ModalNotification
      size={stepsConfig[currentStep].size as Breakpoint}
      fullWidth
      open
      onClose={onClose}
      dialogProps={{
        title: stepsConfig[currentStep].title,
        content: hasErrors ? (
          <Stack spacing={1}>
            {errors.map((error, index) => (
              <Alert severity='error' key={index}>
                {error}
              </Alert>
            ))}
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ py: 1 }}>
            {stepsConfig[currentStep].content}
          </Stack>
        ),
        actions: getActionsSet(),
      }}
    />
  );
};

export default SubmitToFulfilment;
